
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/context/auth/AuthContext';
import { safeToastInfo, safeToastSuccess } from '@/components/ui/toastManager';
import {
  getTutorialPreferences,
  saveTutorialPreferences,
  TutorialPreferences
} from '@/services/tutorialPreferences.service';


interface TutorialContextType {
  preferences: TutorialPreferences;
  isLoading: boolean;
  showTutorial: (pageId: string) => void;
  dismissTutorial: (pageId: string) => void;
  setGlobalEnabled: (enabled: boolean) => Promise<void>;
  resetAllTutorials: () => Promise<void>;
}

const defaultPreferences: TutorialPreferences = {
  globalEnabled: true,
  seenTutorials: {}
};

const TutorialContext = createContext<TutorialContextType>({
  preferences: defaultPreferences,
  isLoading: true,
  showTutorial: () => {},
  dismissTutorial: () => {},
  setGlobalEnabled: async () => {},
  resetAllTutorials: async () => {},
});

export const useTutorialContext = () => useContext(TutorialContext);

interface TutorialProviderProps {
  children: ReactNode;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<TutorialPreferences>(defaultPreferences);
  const [isLoading, setIsLoading] = useState(true);

  // Carica le preferenze tutorial dal localStorage o da Supabase
  useEffect(() => {
    const loadPreferences = async () => {
      setIsLoading(true);
      
      // Prima controlla il localStorage per le preferenze offline
      const localPrefs = localStorage.getItem('tutorial-preferences');
      let currentPrefs = localPrefs ? JSON.parse(localPrefs) : { ...defaultPreferences };
      
      // Se l'utente è autenticato, prova a caricare le preferenze (anche offline)
      if (user) {
        const remotePrefs = await getTutorialPreferences(user.id);
        if (remotePrefs) {
          currentPrefs = {
            ...currentPrefs,
            ...remotePrefs
          };
        }
      }
      
      setPreferences(currentPrefs);
      setIsLoading(false);
    };
    
    loadPreferences();
  }, [user]);

  // Salva le preferenze quando cambiano
  const savePreferences = async (newPrefs: TutorialPreferences) => {
    // Salva sempre in localStorage
    localStorage.setItem('tutorial-preferences', JSON.stringify(newPrefs));
    
    // Se l'utente è autenticato, delega al servizio che gestisce anche l'offline
    if (user) {
      const success = await saveTutorialPreferences(user.id, newPrefs);
      if (!success) {
        console.error('[TutorialContext] Errore nel salvataggio delle preferenze');
      }
    }
    
    setPreferences(newPrefs);
  };

  const showTutorial = (pageId: string) => {
    const newPrefs = {
      ...preferences,
      seenTutorials: {
        ...preferences.seenTutorials,
        [pageId]: false
      }
    };
    savePreferences(newPrefs);
  };

  const dismissTutorial = (pageId: string) => {
    const newPrefs = {
      ...preferences,
      seenTutorials: {
        ...preferences.seenTutorials,
        [pageId]: true
      }
    };
    savePreferences(newPrefs);
  };

  const setGlobalEnabled = async (enabled: boolean) => {
    const newPrefs = {
      ...preferences,
      globalEnabled: enabled
    };
    await savePreferences(newPrefs);
    
    if (enabled) {
      safeToastSuccess("Guide tutorial attivate");
    } else {
      safeToastInfo("Guide tutorial disattivate");
    }
  };

  const resetAllTutorials = async () => {
    const newPrefs = {
      ...preferences,
      seenTutorials: {}
    };
    await savePreferences(newPrefs);
    safeToastSuccess("Tutte le guide sono state reimpostate");
  };

  return (
    <TutorialContext.Provider
      value={{
        preferences,
        isLoading,
        showTutorial,
        dismissTutorial,
        setGlobalEnabled,
        resetAllTutorials
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};
